import React, { useState, useEffect } from "react";

// Child components
import Form from "./Childs/Form";

// Common components
import FormCard from "../../Common/Childs/Structure/FormCard";
import Description from "../../Common/Childs/Structure/Description";
import Title from "../../Common/Childs/Structure/Title";
import { useGlobal } from "../../../context/GlobalContextProvider";
import { useWallet } from "../../../context/WalletContextProvider";

// Initial state for form data
const INITIAL_FORM_STATE = {
  ct_about_title_1: "",
  ct_about_title_2: "",
  ct_about_title_3: "",
  ct_about_title_4: "",
  ct_about_icon_1: "",
  ct_about_icon_2: "",
  ct_about_icon_3: "",
  ct_about_icon_4: "",
  ct_about_description_1: "",
  ct_about_description_2: "",
  ct_about_description_3: "",
  ct_about_description_4: "",
  ct_about_faqs: "",
  ct_about_main_description: "",
  ct_about_main_title: "",
  ct_check_about_updated: false,
};

// Initial state for buffered images
const INITIAL_BUFFER_STATE = {
  ct_about_icon_1: "",
  ct_about_icon_2: "",
  ct_about_icon_3: "",
  ct_about_icon_4: "",
};

/**
 * API service for about operations
 */
const aboutAPI = {
  get: (account, campaignId) => fetch(`/api/sponsor/${account}/campaign/about/${campaignId}`),
  update: (account, campaignId, data) =>
    fetch(`/api/sponsor/${account}/campaign/about/${campaignId}`, {
      method: "PUT",
      body: JSON.stringify(data),
      headers: { "Content-type": "application/json; charset=UTF-8" },
    }),
};

/**
 * API service for file operations
 */
const fileAPI = {
  getByName: (name) => fetch(`/api/file/name/${name}`),
};

/**
 * UpdateAbout Component
 * Manages data for the "About" section of a campaign
 */
export default function UpdateAbout() {
  const { account } = useWallet();
  const { campaign, setSpinner } = useGlobal();

  const [formData, setFormData] = useState(INITIAL_FORM_STATE);
  const [imageBuffer, setImageBuffer] = useState(INITIAL_BUFFER_STATE);

  /**
   * Fetch data for the "About" section
   */
  useEffect(() => {
    const getData = async () => {
      if (!account || !campaign) return;

      setSpinner(true);
      try {
        const res = await aboutAPI.get(account, campaign.campaign_id);

        if (res.status === 200) {
          const json = await res.json();

          // Load icons asynchronously into the image buffer
          const newImageBuffer = { ...INITIAL_BUFFER_STATE };
          for (const icon of Object.keys(INITIAL_BUFFER_STATE)) {
            if (json[icon]?.length > 0) {
              try {
                const req = await fileAPI.getByName(json[icon]);
                const data = await req.json();
                newImageBuffer[icon] = data;
              } catch (error) {
                console.error(`Error loading icon ${icon}:`, error);
              }
            }
          }
          setImageBuffer(newImageBuffer);

          // Set form data with received values or defaults
          setFormData({
            ct_about_title_1: json.ct_about_title_1 || "",
            ct_about_title_2: json.ct_about_title_2 || "",
            ct_about_title_3: json.ct_about_title_3 || "",
            ct_about_title_4: json.ct_about_title_4 || "",
            ct_about_icon_1: json.ct_about_icon_1 || "",
            ct_about_icon_2: json.ct_about_icon_2 || "",
            ct_about_icon_3: json.ct_about_icon_3 || "",
            ct_about_icon_4: json.ct_about_icon_4 || "",
            ct_about_description_1: json.ct_about_description_1 || "",
            ct_about_description_2: json.ct_about_description_2 || "",
            ct_about_description_3: json.ct_about_description_3 || "",
            ct_about_description_4: json.ct_about_description_4 || "",
            ct_about_faqs: json.ct_about_faqs || "",
            ct_about_main_description: json.ct_about_main_description || "",
            ct_about_main_title: json.ct_about_main_title || "",
            campaign_id: json.campaign_id,
            campaign_name: json.campaign_name,
          });
        }
      } catch (error) {
        console.error("Error fetching about data:", error);
      } finally {
        setSpinner(false);
      }
    };

    getData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account, campaign]);

  /**
   * Handle form submission
   */
  const handleSubmit = async (data) => {
    setSpinner(true);
    try {
      await aboutAPI.update(account, campaign?.campaign_id, data);
    } catch (error) {
      console.error("Error updating about data:", error);
    } finally {
      setSpinner(false);
    }
  };

  return (
    <>
      <Title title="About" /> {/* Component title */}
      <Description text={'Add contents to the “About” section of your Campaign website.'} />

      {/* FormCard wraps and displays the Form component */}
      <FormCard>
        <Form
          formData={formData}
          handleSubmit={handleSubmit}
          imageBuffer={imageBuffer}
          setSpinner={setSpinner}
        />
      </FormCard>
    </>
  );
}
